import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { MaskedHeading, Fade, FadeUp, ImageReveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import usePageMeta from "@/lib/usePageMeta";

const CHAPTERS = [
  {
    n: "01",
    t: "Vi har valgt at være specialister",
    d: "Hagelund er ikke den klassiske tømrervirksomhed, der laver lidt af det hele. Vi har valgt at specialisere os i døre og vinduer, fordi de rigtige løsninger kan gøre en markant forskel for både husets udtryk, komfort og funktion. Specialiseringen giver os mulighed for at gå grundigere ind i rådgivningen og have blik for de detaljer, der får den samlede løsning til at lykkes.",
  },
  {
    n: "02",
    t: "Rådgivningen begynder med dit hus",
    d: "Vi starter ikke med at pege på et bestemt produkt. Først ser vi på husets arkitektur, lysindfald, eksisterende materialer og de behov, løsningen skal opfylde i hverdagen. Det giver et bedre grundlag for at vælge rigtigt og sikrer, at de nye døre og vinduer ikke blot passer i åbningerne, men føles som en naturlig del af huset.",
  },
  {
    n: "03",
    t: "Tryghed hele vejen gennem projektet",
    d: "For os handler kvalitet ikke kun om selve døren eller vinduet. Det handler også om ærlig rådgivning, klare forventninger, god kommunikation og en udførelse, hvor der er styr på detaljerne. Du skal vide, hvad der skal ske, hvorfor vi anbefaler den valgte løsning, og hvad du kan forvente af det færdige resultat.",
  },
  {
    n: "04",
    t: "Målet er mere end nye døre og vinduer",
    d: "Det færdige resultat skal kunne mærkes i hverdagen og ses på huset. Det skal skabe et bedre lysindfald, øge komforten og understøtte boligens arkitektur — samtidig med at materialer og udførelse holder mange år frem. Hos Hagelund er opgaven først lykkedes, når løsningen løfter hele hjemmet visuelt, funktionelt og værdimæssigt.",
  },
];

export default function About() {
  usePageMeta({
    title: "Om os",
    description:
      "Om Hagelund ApS — specialister i døre og vinduer. Ærlig rådgivning ud fra dit hus og en gennemtænkt løsning fra første møde til sidste finish.",
    path: "/om-os",
  });

  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 pt-36 md:pt-48 pb-16 md:pb-24">
        <Fade>
          <p className="text-xs uppercase tracking-label text-accent mb-6">Om Hagelund</p>
        </Fade>
        <h1 className="font-display font-light leading-[0.9] text-5xl sm:text-7xl md:text-[7.5rem] tracking-tight max-w-5xl">
          <MaskedHeading lines={["Specialister", "i døre og vinduer"]} delay={0.2} />
        </h1>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-12">
        <ImageReveal src="/images/skovvej-6.webp" alt="Hagelund projekt" className="aspect-[16/9] md:aspect-[21/9]" />
      </section>

      <Marquee />

      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-40">
        <div className="max-w-4xl mx-auto space-y-20 md:space-y-28">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-baseline">
                <span className="md:col-span-3 font-display text-7xl md:text-8xl font-light text-muted-foreground/40">{c.n}</span>
                <div className="md:col-span-9">
                  <h2 className="font-display text-3xl md:text-5xl font-light leading-tight text-balance">{c.t}</h2>
                  <p className="mt-6 text-muted-foreground font-light text-lg">{c.d}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="bg-foreground text-background py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 text-center">
          <FadeUp>
            <h2 className="font-display text-4xl md:text-6xl font-light max-w-3xl mx-auto text-balance">
              Skal vi løfte dit hjem sammen?
            </h2>
            <Link to="/kontakt" data-testid="about-cta" className="group inline-flex items-center gap-3 bg-background text-foreground mt-10 px-10 py-5 text-xs uppercase tracking-label font-semibold hover:bg-accent hover:text-accent-foreground transition-colors">
              Book rådgivning <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
